import { formatMembershipName } from '@provision/service-level-utils';
import { useEffect, useMemo } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { FormPriceField } from 'src/components/Forms/FormPriceField/form-price-field';
import { FormSelect } from 'src/components/Forms/FormSelect/form-select';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { CustomSignupLinkIntervalUnit, OfferedProduct } from 'src/graphql';

export interface CustomSignupLinkFormValues {
  partnerProductId: string;
  amountDollars: number | '';
  intervalUnit: CustomSignupLinkIntervalUnit;
}

enum CustomSignupLinkFieldName {
  PartnerProductId = 'partnerProductId',
  AmountDollars = 'amountDollars',
  IntervalUnit = 'intervalUnit',
}

const INVALID_PRICE_MESSAGE = 'Please enter a valid price.';

const intervalOptions = [
  { id: 'month', label: 'Monthly', value: 'month' },
  { id: 'year', label: 'Annual', value: 'year' },
];

function validatePrice(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? true
    : INVALID_PRICE_MESSAGE;
}

interface CustomSignupLinkFormProps {
  formMethods: UseFormReturn<CustomSignupLinkFormValues>;
  offeredProducts: OfferedProduct[];
  isSubmitting: boolean;
}

interface CustomSignupLinkFieldsProps {
  offeredProducts: OfferedProduct[];
  isSubmitting: boolean;
}

const CustomSignupLinkFields = ({
  offeredProducts,
  isSubmitting,
}: CustomSignupLinkFieldsProps) => {
  const { setValue, watch } = useFormContext<CustomSignupLinkFormValues>();

  const sortedProducts = useMemo(
    () => offeredProducts.slice().sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [offeredProducts],
  );
  const productOptions = useMemo(
    () =>
      sortedProducts.map((product) => ({
        id: product.partnerProductId,
        label: formatMembershipName(product.displayName),
        value: product.partnerProductId,
      })),
    [sortedProducts],
  );

  const selectedProductId = watch(CustomSignupLinkFieldName.PartnerProductId);

  useEffect(() => {
    if (!selectedProductId && sortedProducts[0]?.partnerProductId) {
      setValue(
        CustomSignupLinkFieldName.PartnerProductId,
        sortedProducts[0].partnerProductId,
        {
          shouldDirty: false,
        },
      );
    }
  }, [selectedProductId, setValue, sortedProducts]);

  return (
    <>
      <FormSelect
        type="select"
        name={CustomSignupLinkFieldName.PartnerProductId}
        label="Product"
        options={productOptions}
        required="Please select a product."
        showErrorText
        data-testid="custom-signup-link-product"
        selectProps={{
          disabled: isSubmitting,
        }}
      />

      <FormPriceField
        name={CustomSignupLinkFieldName.AmountDollars}
        label="Custom price"
        required={INVALID_PRICE_MESSAGE}
        rules={{ validate: validatePrice }}
        description="Charged in the partner billing currency."
        showErrorText
        disabled={isSubmitting}
        data-testid="custom-signup-link-price"
        placeholder="0.00"
      />

      <FormSelect
        type="select"
        name={CustomSignupLinkFieldName.IntervalUnit}
        label="Billing interval"
        options={intervalOptions}
        data-testid="custom-signup-link-interval"
        selectProps={{
          disabled: isSubmitting,
        }}
      />
    </>
  );
};

export const CustomSignupLinkForm = ({
  formMethods,
  offeredProducts,
  isSubmitting,
}: CustomSignupLinkFormProps) => (
  <GenericForm<CustomSignupLinkFormValues> formMethods={formMethods}>
    <CustomSignupLinkFields offeredProducts={offeredProducts} isSubmitting={isSubmitting} />
  </GenericForm>
);
